import React from 'react';
import { Toaster as Sonner } from 'sonner';
import { Icons } from '../Icons';

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Astra: modern, COMPACT alert style. Instead of Sonner's wide full-colour "richColors" cards
// (which read as the old/heavy toast), use a narrow dark glass card with a coloured type icon.
// Tighter padding + smaller type = a sleek, unobtrusive notification. Colours are inline (the
// ui-next Tailwind palette doesn't ship sky/emerald/amber-400), so the type cue always renders.
const TINT = { info: '#38bdf8', success: '#34d399', warning: '#fbbf24', error: '#f87171' };
const tinted = (node: React.ReactNode, color: string) => (
  <span style={{ color, display: 'inline-flex' }}>{node}</span>
);

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      className="toaster group"
      theme="dark"
      position="bottom-right"
      gap={8}
      offset={12}
      loadingIcon={<Icons.LoadingSpinner className="h-4 w-4" />}
      icons={{
        warning: tinted(<Icons.StatusWarning className="h-4 w-4" />, TINT.warning),
        info: tinted(<Icons.Info className="h-4 w-4" />, TINT.info),
        success: tinted(<Icons.StatusSuccess className="h-4 w-4" />, TINT.success),
        error: tinted(<Icons.StatusError className="h-4 w-4" />, TINT.error),
      }}
      toastOptions={{
        duration: 4000,
        classNames: {
          toast:
            'group flex w-full items-start gap-2.5 rounded-xl border border-white/10 ' +
            'border-l-2 !bg-popover/90 px-3 py-2.5 shadow-xl shadow-black/40 backdrop-blur-md',
          icon: 'mt-px shrink-0',
          title: 'text-[13px] font-semibold leading-tight text-foreground',
          description: 'mt-0.5 text-[12px] leading-snug text-muted-foreground',
          actionButton:
            'ml-auto rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground',
          closeButton: 'border-white/10 bg-popover text-muted-foreground hover:text-foreground',
        },
        style: { width: '320px' },
      }}
      {...props}
    />
  );
};

export { Toaster };
